import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { toast } from 'sonner'
import { useLockedFieldHint } from '../../contexts/LockedFieldHintContext'
import { getDropdownPortalRoot } from '../../utils/dropdownPortal'

interface MultiSelectProps {
  values: string[]
  options: string[]
  onChange: (values: string[]) => void
  onCreateOption?: (value: string) => void | Promise<void>
  placeholder?: string
  addLabel?: string
  className?: string
  inputClassName?: string
  /** When true, dropdown does not open (use with Edit mode). */
  interactionLocked?: boolean
  lockedHint?: string
  searchPlaceholder?: string
  createModalTitle?: string
  createFieldLabel?: string
  createInputPlaceholder?: string
  triggerIcon?: React.ReactNode
  /** When set (e.g. 1), at most this many options can be selected; adding replaces previous selection. */
  maxSelections?: number
  /** Display string for each option in the list; defaults to the option value. */
  getOptionLabel?: (option: string) => string
  /** Closed trigger label when there is a selection; defaults to comma-joined getOptionLabel(option) values. */
  getTriggerLabel?: (selected: string[]) => string
  /** Optional accessible name for the trigger button (e.g. country calling code). */
  ariaLabel?: string
  /** Render the dropdown in a body portal (escapes overflow clipping containers). */
  dropdownPortal?: boolean
  /** Notifies parent when the dropdown opens/closes (e.g. to raise grid row z-index). */
  onOpenChange?: (open: boolean) => void
}

export default function MultiSelect({
  values,
  options,
  onChange,
  onCreateOption,
  placeholder = 'Select...',
  addLabel = 'Add option',
  className,
  inputClassName,
  interactionLocked,
  lockedHint,
  searchPlaceholder = 'Search...',
  createModalTitle,
  createFieldLabel = 'Name',
  createInputPlaceholder = 'Enter name',
  triggerIcon,
  maxSelections,
  getOptionLabel,
  getTriggerLabel,
  ariaLabel,
  dropdownPortal = true,
  onOpenChange
}: MultiSelectProps) {
  const { showAtAnchor } = useLockedFieldHint()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [modalError, setModalError] = useState<string | null>(null)
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const modalInputRef = useRef<HTMLInputElement | null>(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 280 })

  const modalTitle = createModalTitle ?? addLabel

  const updateMenuPos = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    // Match max-h-64 (+ search header + padding). Prefer below; flip above near viewport bottom.
    const estimatedMenuHeight = 280
    const gap = 4
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap
    const openAbove = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow
    const measured =
      dropdownRef.current?.getBoundingClientRect().height ?? estimatedMenuHeight
    const available = openAbove ? spaceAbove : spaceBelow
    const maxHeight = Math.max(160, Math.min(estimatedMenuHeight, available - gap))
    const top = openAbove
      ? Math.max(gap, rect.top - Math.min(measured, maxHeight) - gap)
      : rect.bottom + gap
    const width = Math.max(rect.width, 160)
    const maxLeft = Math.max(gap, window.innerWidth - width - gap)
    const left = Math.min(Math.max(gap, rect.left), maxLeft)
    setMenuPos({ top, left, width, maxHeight })
  }, [])

  useEffect(() => {
    onOpenChange?.(open)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open || !dropdownPortal) return
    const handleReposition = () => updateMenuPos()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [open, dropdownPortal, updateMenuPos])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!createModalOpen) return
    const t = window.setTimeout(() => modalInputRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [createModalOpen])

  useEffect(() => {
    if (!createModalOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createSubmitting) {
        setCreateModalOpen(false)
        setNewItemName('')
        setModalError(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [createModalOpen, createSubmitting])

  const labelFor = useCallback(
    (option: string) => (getOptionLabel ? getOptionLabel(option) : option),
    [getOptionLabel]
  )

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (option) =>
        option.toLowerCase().includes(q) || labelFor(option).toLowerCase().includes(q)
    )
  }, [options, query, labelFor])

  useEffect(() => {
    if (!open || !dropdownPortal) return
    updateMenuPos()
    const raf = window.requestAnimationFrame(() => updateMenuPos())
    return () => window.cancelAnimationFrame(raf)
  }, [open, dropdownPortal, updateMenuPos, filteredOptions.length, query])

  const toggleValue = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((item) => item !== value))
    } else if (maxSelections === 1) {
      onChange([value])
    } else {
      onChange([...values, value])
    }
  }

  const displayValue =
    values.length === 0
      ? placeholder
      : getTriggerLabel
        ? getTriggerLabel(values)
        : values.map((v) => labelFor(v)).join(', ')

  const openCreateModal = () => {
    if (!onCreateOption || interactionLocked) return
    setOpen(false)
    setNewItemName('')
    setModalError(null)
    setCreateModalOpen(true)
  }

  const closeCreateModal = () => {
    if (createSubmitting) return
    setCreateModalOpen(false)
    setNewItemName('')
    setModalError(null)
  }

  const handleModalSubmit = async () => {
    if (!onCreateOption) return
    const trimmed = newItemName.trim()
    if (!trimmed) {
      setModalError(`Please enter ${createFieldLabel.toLowerCase()}.`)
      return
    }
    if (options.some((option) => option.toLowerCase() === trimmed.toLowerCase())) {
      setModalError('This value already exists in the list.')
      return
    }
    setModalError(null)
    setCreateSubmitting(true)
    try {
      await Promise.resolve(onCreateOption(trimmed))
      setCreateModalOpen(false)
      setNewItemName('')
      setQuery('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      toast.error(message)
    } finally {
      setCreateSubmitting(false)
    }
  }

  const createModal =
    createModalOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="multiselect-create-title"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !createSubmitting) closeCreateModal()
        }}
      >
        <div
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <h2 id="multiselect-create-title" className="text-lg font-semibold text-gray-900">
              {modalTitle}
            </h2>
            <button
              type="button"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              onClick={closeCreateModal}
              disabled={createSubmitting}
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <label className="mt-4 block text-sm font-semibold text-gray-900">{createFieldLabel}</label>
          <input
            ref={modalInputRef}
            className={clsx('input-primary mt-2', modalError && 'border-error-500 focus:border-error-500 focus:ring-error-500')}
            placeholder={createInputPlaceholder}
            value={newItemName}
            disabled={createSubmitting}
            onChange={(e) => {
              setNewItemName(e.target.value)
              if (modalError) setModalError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleModalSubmit()
              }
            }}
          />
          {modalError && <p className="mt-2 text-xs text-error-600">{modalError}</p>}
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={closeCreateModal} disabled={createSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary min-w-[7rem] justify-center"
              onClick={() => void handleModalSubmit()}
              disabled={createSubmitting}
            >
              {createSubmitting ? 'Adding…' : addLabel}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )

  const dropdownMenu = (
    <div
      ref={dropdownPortal ? dropdownRef : undefined}
      className={clsx(
        'pointer-events-auto flex flex-col rounded-md border border-gray-200 bg-white shadow-xl',
        dropdownPortal ? 'fixed' : 'absolute top-full z-40 mt-1 w-full max-h-64'
      )}
      style={
        dropdownPortal
          ? {
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: 999999
            }
          : undefined
      }
    >
      <div className="shrink-0 border-b border-gray-100 px-3 py-2">
        <input
          className="input-primary"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        {filteredOptions.length === 0 && (
          <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
        )}
        {filteredOptions.map((option) => (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={values.includes(option)}
              onChange={() => toggleValue(option)}
              className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
            />
            <span>{labelFor(option)}</span>
          </label>
        ))}
      </div>
      {onCreateOption && (
        <div className="shrink-0 border-t border-gray-100 bg-white px-3 py-2">
          <button
            type="button"
            className={clsx(
              'flex w-full items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700',
              interactionLocked && 'cursor-not-allowed opacity-50'
            )}
            onClick={() => {
              if (interactionLocked) return
              openCreateModal()
            }}
          >
            <PlusIcon className="h-4 w-4" />
            <span>{addLabel}</span>
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div
      ref={containerRef}
      className={clsx('relative', className, open && !interactionLocked && 'z-[9999]')}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-disabled={interactionLocked || undefined}
        className={clsx(
          'input-primary flex w-full items-center justify-between gap-3 text-left',
          inputClassName,
          interactionLocked && 'cursor-not-allowed bg-page-bg text-heritage-grey opacity-70'
        )}
        onClick={(e) => {
          if (interactionLocked) {
            if (lockedHint) showAtAnchor(e.currentTarget, lockedHint)
            return
          }
          setOpen((prev) => !prev)
        }}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {triggerIcon ? <span className="shrink-0 text-gray-400">{triggerIcon}</span> : null}
          <span
            className={clsx(
              'min-w-0 truncate text-sm',
              interactionLocked
                ? 'text-heritage-grey'
                : values.length
                  ? 'text-gray-900'
                  : 'text-gray-400'
            )}
          >
            {displayValue}
          </span>
        </span>
        <ChevronDownIcon
          className={clsx(
            'h-4 w-4 shrink-0 transition-transform',
            interactionLocked ? 'text-heritage-grey/70' : 'text-gray-400',
            open && !interactionLocked && 'rotate-180'
          )}
        />
      </button>

      {open && !dropdownPortal ? dropdownMenu : null}
      {open && dropdownPortal ? createPortal(dropdownMenu, getDropdownPortalRoot()) : null}

      {createModal}
    </div>
  )
}
